import { appModules } from '../../../config/appModules'

export type ModulePermission = 'hide' | 'view' | 'edit'

export interface UserModule {
  name: string
  code: string
  permission: ModulePermission
}

interface UserModuleSource {
  name: string
  code: string
}

const baseUserModuleSources: UserModuleSource[] = appModules.map(
  (moduleItem) => ({
    name: moduleItem.name,
    code: moduleItem.code,
  }),
)

const toCamelCase = (value: string): string => {
  return value.replace(/[-_]+([a-zA-Z0-9])/g, (_, nextChar: string) =>
    nextChar.toUpperCase(),
  )
}

const getLegacyModuleCode = (path: string): string => {
  const segments = path.split('/').filter(Boolean)

  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index]

    if (!segment.startsWith(':') && segment !== '*') {
      return toCamelCase(segment)
    }
  }

  return toCamelCase(path)
}

const permissionValues: ModulePermission[] = ['hide', 'view', 'edit']

const normalizePermission = (value: unknown): ModulePermission => {
  return typeof value === 'string' &&
    permissionValues.includes(value as ModulePermission)
    ? (value as ModulePermission)
    : 'hide'
}

const moduleSourcesByCode = new Map(
  baseUserModuleSources.map((moduleSource) => [
    moduleSource.code,
    moduleSource,
  ]),
)

const moduleSourcesByLegacyCode = new Map(
  baseUserModuleSources.map((moduleSource) => [
    getLegacyModuleCode(moduleSource.code),
    moduleSource,
  ]),
)

export const userModuleDefinitions = baseUserModuleSources

export const createDefaultUserModules = (): UserModule[] => {
  return userModuleDefinitions.map((moduleSource) => ({
    ...moduleSource,
    permission: 'hide',
  }))
}

export const normalizeUserModules = (value: unknown): UserModule[] => {
  const permissionsByCode = new Map<string, ModulePermission>()
  const storedCodes = new Set<string>()

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (typeof item === 'string') {
        const matchedModule =
          moduleSourcesByCode.get(item) || moduleSourcesByLegacyCode.get(item)

        if (matchedModule) {
          storedCodes.add(matchedModule.code)
          permissionsByCode.set(matchedModule.code, 'view')
        }

        return
      }

      if (!item || typeof item !== 'object') {
        return
      }

      const rawCode = 'code' in item ? item.code : undefined
      const rawName = 'name' in item ? item.name : undefined
      const permission = normalizePermission(
        'permission' in item ? item.permission : undefined,
      )

      if (typeof rawCode === 'string') {
        const matchedModule = moduleSourcesByCode.get(rawCode)
        const code = matchedModule?.code || rawCode
        storedCodes.add(code)
        permissionsByCode.set(code, permission)
        return
      }

      if (typeof rawName === 'string') {
        const matchedModule = userModuleDefinitions.find(
          (moduleSource) => moduleSource.name === rawName,
        )

        if (matchedModule) {
          storedCodes.add(matchedModule.code)
          permissionsByCode.set(matchedModule.code, permission)
        }
      }
    })
  }

  const purchasesPermission = permissionsByCode.get('purchases')
  if (purchasesPermission && purchasesPermission !== 'hide') {
    ;['utility-bills', 'spend-analytics'].forEach((code) => {
      if (!storedCodes.has(code)) {
        permissionsByCode.set(code, purchasesPermission)
      }
    })
  }

  return userModuleDefinitions.map((moduleSource) => ({
    ...moduleSource,
    permission: permissionsByCode.get(moduleSource.code) || 'hide',
  }))
}
